import { usePageHeader } from "contexts/PageHeaderContext";
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import {
  DollarSign,
  Plus,
  Edit2,
  Trash2,
  Search,
  Filter,
  X,
  Check,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  HandCoins
} from 'lucide-react';
import { toast } from 'sonner';
import { InputFieldset } from 'ui/inputFieldset';
import { SelectFieldset } from 'ui/selectFieldset';
import { SelectTriggerUI } from 'ui/selectTrigger';
import { TextareaUI } from 'ui/textareaUI';
import { createQarzanType, deleteQarzanType, getQarzanTypeList, updateQarzanType } from 'services/qarzanHassanaService';
import axios from 'axios';
import { FormErrors, LoanType } from 'types/qarzanHassana';
import ConfirmDialog from 'ui/ConfirmDialog';
import { FullScreenLoading } from 'components/ui/loader';
import useDebounce from 'hooks/useDebounce';
import { useAuth } from 'contexts/AuthContext';
import Pagination from "../../ui/pagination";
import { removeSpecialCharacters } from 'utils/commonutils';


interface QarzanHassanaPageProps {
  onNavigate?: (page: string) => void;
}


export default function QarzanHassanaPage({ onNavigate }: QarzanHassanaPageProps) {
  const { setPageHeader } = usePageHeader();
  useEffect(() => {
    setPageHeader({
      title: "Qardan Hassana",
      childTitle: "Manage interest-free loan types and programs",
      icon: <HandCoins className="h-5 w-5" />,
    });
    return () => setPageHeader(null);
  }, [setPageHeader]);

  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [refetch, setRefatch] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [ToDelete, setToDelete] = useState<LoanType | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  // const [showStatistics, setShowStatistics] = useState(false);
  const [showStatistics] = useState(false);
  const [editingItem, setEditingItem] = useState<LoanType | null>(null);

  // Sorting state
  const [sortColumn, setSortColumn] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [loanTypes, setLoanTypes] = useState<LoanType[]>([])
  const [QarzanHassanTotalCount, setQarzanHassanTotalCount] = useState(0);


  const [filters, setFilters] = useState({
    name: '',
    code: '',
    status: 'all',
    pageNo: 1,
    pageSize: 20,
  });

  const mergedFilters = useMemo(
    () => ({
      ...filters,
      searchTerm,
    }),
    [filters, searchTerm],
  );

  const debouncedFilters = useDebounce(mergedFilters, 400);

  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
    maxAmount: '',
    maxInstallments: '',
    status: 'active' as 'active' | 'inactive'
  });

  const [formErrors, setFormErrors] = useState<FormErrors>({
    code: "",
    name: "",
    description: "",
    status: ""
  });



  // USEEFFECT FUNCTION START
  const fetchQarzanTypes = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getQarzanTypeList(debouncedFilters);
      setLoanTypes(data?.items || []);
      setQarzanHassanTotalCount(data?.totalCount || 0);
    } catch {
      toast.error("Failed to fetch draft visa applications");
    } finally {
      setLoading(false);
    }
  }, [debouncedFilters]);

  useEffect(() => {
    fetchQarzanTypes()
  }, [fetchQarzanTypes, refetch]);
  // USEEFFECT FUNCTION END


  // Handle sorting
   const handleSort = (column: string) => {
    console.log('column', column)
    if (sortColumn === column) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };


  const sortedLoanApplicants = useMemo(() => {
    if (!sortColumn) return loanTypes;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return [...loanTypes].sort((a: any, b: any) => {
      const aValue = String(a[sortColumn] ?? "");
      const bValue = String(b[sortColumn] ?? "");

      if (aValue == null) return 1;
      if (bValue == null) return -1;

      const comparison = String(aValue).localeCompare(
        String(bValue),
        undefined,
        { numeric: true, sensitivity: "base" }
      );

      return sortDirection === "asc"
        ? comparison
        : -comparison;
    });
  }, [loanTypes, sortColumn, sortDirection]);


  const handleAdd = () => {
    setEditingItem(null);
    setFormData({
      code: '',
      name: '',
      description: '',
      maxAmount: '',
      maxInstallments: '',
      status: 'active'
    });
    setFormErrors({
      code: "",
      name: "",
      description: "",
      status: ""
    });
    setShowDialog(true);
  };

  const handleEdit = (loan: LoanType) => {
    setEditingItem(loan);
    setFormData({
      code: String(loan?.qarzanCode),
      name: String(loan?.qarzanTypeName),
      description: loan?.description,
      maxAmount: loan.maxAmount == null ? "" : String(loan.maxAmount),
      maxInstallments: loan.maxInstallments == null ? "" : String(loan.maxInstallments),
      status: loan.isActive ? 'active' : 'inactive'
    });
    setFormErrors({
      code: "",
      name: "",
      description: "",
      // eligibility: "",
      status: ""
    });
    setShowDialog(true);
  };


  const handleSave = async () => {
    const errors: FormErrors = {
      code: "",
      name: "",
      description: "",
      // eligibility: "",
      status: ""
    };

    // Validate name
    if (!formData.name || formData.name.trim() === "") {
      errors.name = "Qardan Type Name is required";
    } else if (formData.name.trim().length < 3) {
      errors.name = "Name must be at least 3 characters";
    }

    // Validate code
    if (!formData.code || formData.code.trim() === "") {
      errors.code = "Qardan Code is required";
    } else if (formData.code.trim().length < 2) {
      errors.code = "Code must be at least 2 characters";
    } else if (formData.code.trim().length > 10) {
      errors.code = "Code cannot exceed 10 characters";
    }

    const maxAmount = Number(formData.maxAmount);
    const maxInstallments = Number(formData.maxInstallments);
    if (!formData.maxAmount.trim() || !Number.isFinite(maxAmount) || maxAmount <= 0) {
      errors.maxAmount = "Enter a maximum amount greater than zero";
    }
    if (!formData.maxInstallments.trim() || !Number.isSafeInteger(maxInstallments) || maxInstallments < 0) {
      errors.maxInstallments = "Enter a whole number of months (zero or greater)";
    }

    // Validate status
    if (!formData.status) {
      errors.status = "Status is required";
    }

    setFormErrors(errors);

    if (errors.code || errors.name || errors.status || errors.maxAmount || errors.maxInstallments) {
      toast.error("Please fill all required fields correctly");
      return;
    }


    try {
      // EDIT
      if (editingItem) {
        const response = await updateQarzanType({
          publicId: String(editingItem?.publicId),
          qarzanCode: formData.code,
          qarzanTypeName: formData.name,
          description: formData.description,
          maxAmount,
          maxInstallments,
          isActive: formData.status === 'active' ? true : false,
          updatedBy: user?.id ? String(user.id) : "Admin"
        });
        setLoanTypes((prev) =>
          prev.map((item) =>
            item.publicId === editingItem.publicId
              ? {
                ...item,
                qarzanCode: String(formData.code),
                qarzanTypeName: String(formData.name),
                description: formData.description,
                maxAmount,
                maxInstallments,
                isActive: formData.status === 'active' ? true : false,
              }
              : item
          )
        );
        toast.success(
          response?.message ||
          "Qardan type updated successfully"
        );
      }

      // ADD
      else {
        const response = await createQarzanType({
          qarzanCode: formData.code,
          qarzanTypeName: formData.name,
          description: formData.description,
          maxAmount,
          maxInstallments,
          // status: formData.status,
          // isActive: formData.status === 'active' ? 'true' : 'false',
          createdBy: "Admin"
        });
        setRefatch(!refetch);
        toast.success(
          response?.message ||
          "Qardan type added successfully"
        );
      }
      setShowDialog(false);
    } catch (error: unknown) {
      if (axios.isAxiosError(error) && error.response?.data) {
        const message = error.response.data.message || "Failed to add qardan type";
        toast.error(message);
      }
    }
  };


  const handleDelete = (item: LoanType) => {
    setToDelete(item);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteDraft = async () => {
    if (!ToDelete) return;

    try {
      setLoading(true);
      await deleteQarzanType(String(ToDelete.publicId), String(user?.id));
      setLoanTypes(loanTypes.filter((l) => l.publicId !== ToDelete.publicId));
      toast.success("Qardan type deleted successfully");
      setDeleteDialogOpen(false);
      setToDelete(null);
    } catch (error) {
      toast.error("Failed to delete");
    } finally {
      setLoading(false);
    }
  };

  const cancelDeleteDraft = () => {
    setDeleteDialogOpen(false);
    setToDelete(null);
  };





  return (
    <div className="space-y-6 pb-8">
      {loading && <FullScreenLoading />}
      {/* Statistics Cards */}
      {showStatistics && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="border-blue-200 bg-blue-50/50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-600">Total Qardan Types</p>
                  <p className="text-2xl font-bold text-blue-900">{loanTypes.length}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                  <HandCoins className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-green-200 bg-green-50/50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-600">Active Programs</p>
                  <p className="text-2xl font-bold text-green-900">
                    {loanTypes.filter(l => l.status === 'active').length}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                  <Check className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-purple-200 bg-purple-50/50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-purple-600">Interest Rate</p>
                  <p className="text-2xl font-bold text-purple-900">0%</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-amber-200 bg-amber-50/50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-amber-600">Inactive</p>
                  <p className="text-2xl font-bold text-amber-900">
                    {loanTypes.filter(l => l.status === 'inactive').length}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                  <X className="w-6 h-6 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Search and add action */}
      <div className="flex items-center gap-2">
        <div className="relative min-w-0 flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
          <Input
            aria-label="Search Qardan types"
            placeholder="Search Qardan types, codes, or descriptions..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setFilters(prev => ({ ...prev, pageNo: 1 }));
            }}
            className="h-10 pl-10"
          />
        </div>
        <Button
          onClick={handleAdd}
          style={{ background: '#5B9BD5' }}
          className="h-10 shrink-0 whitespace-nowrap text-white"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Qardan Type
        </Button>
      </div>

      {/* Advanced Filters Section */}
      {showFilters && (
        <Card className="hidden border-[#5B9BD5]/30 bg-gradient-to-br from-sky-50/50 to-blue-50/50">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle style={{ color: '#5B9BD5' }} className="flex items-center gap-2 text-lg">
                  <Filter className="w-5 h-5" />
                  Advanced Filters
                </CardTitle>
                <CardDescription className="mt-1">
                  Filter Qardan types by multiple criteria
                </CardDescription>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowFilters(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Loan Name Filter */}
              <div className="space-y-2">
                <Label htmlFor="filter-name" className="text-sm">Qardan Type Name</Label>
                <Input
                  id="filter-name"
                  placeholder="Search by name..."
                  value={filters.name}
                  onChange={(e) => setFilters({ ...filters, name: e.target.value })}
                  className="h-9 text-sm"
                />
              </div>

              {/* Code Filter */}
              <div className="space-y-2">
                <Label htmlFor="filter-code" className="text-sm">Qardan Code</Label>
                <Input
                  id="filter-code"
                  placeholder="Search by code..."
                  value={filters.code}
                  onChange={(e) => setFilters({ ...filters, code: e.target.value })}
                  className="font-mono h-9 text-sm"
                />
              </div>

              {/* Status Filter */}
              <div className="space-y-2">
                <Label htmlFor="filter-status" className="text-sm">Status</Label>
                <Select value={filters.status} onValueChange={(value) => setFilters({ ...filters, status: value })}>
                  <SelectTrigger id="filter-status" className="h-9 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="active">Active Only</SelectItem>
                    <SelectItem value="inactive">Inactive Only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Active Filters Summary & Clear Button */}
            {(filters.name || filters.code || filters.status !== 'all') && (
              <div className="flex items-center justify-between gap-3 p-3 bg-white border border-[#5B9BD5]/30 rounded-lg">
                <div className="flex-1">
                  <p className="text-sm font-medium text-[#5B9BD5] mb-2">Active Filters:</p>
                  <div className="flex flex-wrap gap-1.5">
                    {filters.name && (
                      <Badge variant="outline" className="border-[#5B9BD5]/30 text-[#5B9BD5] text-xs">
                        Name: {filters.name}
                      </Badge>
                    )}
                    {filters.code && (
                      <Badge variant="outline" className="border-[#5B9BD5]/30 text-[#5B9BD5] text-xs">
                        Code: {filters.code}
                      </Badge>
                    )}
                    {filters.status !== 'all' && (
                      <Badge variant="outline" className="border-[#5B9BD5]/30 text-[#5B9BD5] text-xs">
                        Status: {filters.status}
                      </Badge>
                    )}
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setFilters({
                      name: '',
                      code: '',
                      status: 'all',
                      pageNo: 1,
                      pageSize: 20,
                    });
                  }}
                  className="text-red-600 border-red-300 hover:bg-red-50 flex-shrink-0"
                >
                  <X className="w-4 h-4 mr-1" />
                  Clear All
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Loan Types Table */}
      <Card className="border-[#5B9BD5]/30 bg-white">
        <CardHeader className="border-b bg-gradient-to-r from-sky-50 to-blue-50 p-4 rounded-t-xl">
          <CardTitle style={{ color: '#5B9BD5' }}>Qardan Hasana</CardTitle>
          <CardDescription>
            {loanTypes?.length} Qardan type{loanTypes?.length !== 1 ? 's' : ''} found
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow style={{ backgroundColor: '#E3F2FD' }}>
                  {/* <TableHead style={{ color: '#5B9BD5' }} className="px-4 py-3 text-xs">#</TableHead> */}
                  <TableHead
                    style={{ color: '#5B9BD5' }}
                    className="px-4 py-3 text-xs cursor-pointer select-none hover:bg-sky-100/50"
                    onClick={() => handleSort('qarzanCode')}
                  >
                    <div className="flex items-center gap-1">
                      Code
                      {sortColumn === 'qarzanCode' ? (
                        sortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                      ) : (
                        <ArrowUpDown className="w-3 h-3 opacity-30" />
                      )}
                    </div>
                  </TableHead>
                  <TableHead
                    style={{ color: '#5B9BD5' }}
                    className="px-4 py-3 text-xs cursor-pointer select-none hover:bg-sky-100/50"
                    onClick={() => handleSort('qarzanTypeName')}
                  >
                    <div className="flex items-center gap-1">
                      Qardan Hasana
                      {sortColumn === 'qarzanTypeName' ? (
                        sortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                      ) : (
                        <ArrowUpDown className="w-3 h-3 opacity-30" />
                      )}
                    </div>
                  </TableHead>
                  {([
                    ['maxAmount', 'Max Amount ($)'],
                    ['maxInstallments', 'Max Installments (Months)'],
                  ] as const).map(([column, label]) => (
                    <TableHead key={column} style={{ color: '#5B9BD5' }} className="px-4 py-3 text-xs">
                      <button type="button" className="flex items-center gap-1" onClick={() => handleSort(column)}>
                        {label}
                        {sortColumn === column ? (
                          sortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                        ) : <ArrowUpDown className="w-3 h-3 opacity-30" />}
                      </button>
                    </TableHead>
                  ))}
                  <TableHead style={{ color: '#5B9BD5' }} className="px-4 py-3 text-xs">Description</TableHead>
                  <TableHead style={{ color: '#5B9BD5' }} className="px-4 py-3 text-xs">Status</TableHead>
                  <TableHead style={{ color: '#5B9BD5' }} className="px-4 py-3 text-xs text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loanTypes?.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                      No Qardan types found
                    </TableCell>
                  </TableRow>
                ) : (
                  loanTypes && sortedLoanApplicants?.map((loan, index) => (
                    <TableRow key={loan.id} className="hover:bg-gray-50">
                      {/* <TableCell className="px-4 py-3 text-xs text-gray-500">{index + 1}</TableCell> */}
                      <TableCell className="px-4 py-3 text-xs">
                        <Badge className="bg-blue-100 text-blue-700 border border-blue-300 text-xs px-2">
                          {loan.qarzanCode}
                        </Badge>
                      </TableCell>
                      <TableCell className="px-4 py-3 text-xs font-medium">{loan.qarzanTypeName}</TableCell>
                      <TableCell className="px-4 py-3 text-xs">
                        {loan.maxAmount == null ? '—' : Number(loan.maxAmount).toLocaleString('en-US', { maximumFractionDigits: 2 })}
                      </TableCell>
                      <TableCell className="px-4 py-3 text-xs font-semibold text-sky-700">
                        {loan.maxInstallments ?? '—'}
                      </TableCell>
                      <TableCell className="px-4 py-3 text-xs text-gray-600 max-w-xs truncate">
                        {loan.description}
                      </TableCell>
                      <TableCell className="px-4 py-3 text-xs">
                        {loan.isActive ? (
                          <Badge className="bg-green-100 text-green-700 border border-green-300 text-xs px-2">
                            Active
                          </Badge>
                        ) : (
                          <Badge className="bg-gray-100 text-gray-700 border border-gray-300 text-xs px-2">
                            Inactive
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="px-4 py-3 text-xs text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(loan)}
                            className="h-8 px-2 hover:bg-sky-50"
                          >
                            <Edit2 className="w-4 h-4 text-[#5B9BD5]" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(loan)}
                            className="h-8 px-2 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4 text-red-600" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* PAGINATION COMPONENT FOR DRAFT */}
          <Pagination
            totalItems={QarzanHassanTotalCount}
            itemsPerPage={filters.pageSize}
            currentPage={filters.pageNo}
            onPageChange={(page) =>
              setFilters((prev) => ({
                ...prev,
                pageNo: page, // convert back to 0-based
              }))
            }
            onPageSizeChange={(size) =>
              setFilters((prev) => ({
                ...prev,
                pageSize: size,
                pageNo: 1, // IMPORTANT: reset page
              }))
            }
            label=""
          />
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={(open) => {
        setShowDialog(open);
        if (!open) {
          // Clear errors when dialog closes
          setFormErrors({
            code: "",
            name: "",
            description: "",
            status: ""
          });
        }
      }}>
        <DialogContent className="max-w-2xl bg-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingItem ? 'Edit Qardan Type' : 'Add New Qardan Type'}
            </DialogTitle>
            <DialogDescription>
              {editingItem ? 'Update the Qardan type details below' : 'Fill in the details to create a new Qardan type'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <InputFieldset
                  label="Qardan Code"
                  required
                  id="code"
                  value={formData.code}
                  onChange={(e) => {
                    setFormData({ ...formData, code: removeSpecialCharacters(e.target.value) });
                    // Clear error on change
                    if (formErrors.code) {
                      setFormErrors({ ...formErrors, code: "" });
                    }
                  }}
                  placeholder="e.g., BUS, EDU"
                  className={formErrors.code ? "border-red-500" : ""}
                />
                {formErrors.code && (
                  <p className="text-red-500 text-xs">{formErrors.code}</p>
                )}
              </div>
              <div className="space-y-2">
                <SelectFieldset label="Status" required>
                  <Select
                    value={formData.status}
                    onValueChange={(value: "active" | "inactive") => {
                      setFormData({ ...formData, status: value });
                      // Clear error on change
                      if (formErrors.status) {
                        setFormErrors({ ...formErrors, status: "" });
                      }
                    }}
                  >
                    <SelectTriggerUI className={formErrors.status ? "border-red-500" : ""}>
                      <SelectValue />
                    </SelectTriggerUI>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </SelectFieldset>
                {formErrors.status && (
                  <p className="text-red-500 text-xs">{formErrors.status}</p>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <InputFieldset
                label="Qardan Type Name"
                required
                id="name"
                value={formData.name}
                onChange={(e) => {
                  setFormData({ ...formData, name: removeSpecialCharacters(e.target.value) });
                  // Clear error on change
                  if (formErrors.name) {
                    setFormErrors({ ...formErrors, name: "" });
                  }
                }}
                placeholder="e.g., Business Qardan Hasana"
                className={formErrors.name ? "border-red-500" : ""}
              />
              {formErrors.name && (
                <p className="text-red-500 text-xs">{formErrors.name}</p>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {([
                ['maxAmount', 'Max Amount ($)', 'Enter maximum amount'],
                ['maxInstallments', 'Max Installments (Months)', 'Enter maximum installment months'],
              ] as const).map(([field, label, placeholder]) => (
                <div key={field} className="space-y-2">
                  <InputFieldset
                    label={label}
                    aria-label={label}
                    required
                    aria-required="true"
                    id={field}
                    type="number"
                    min={field === 'maxAmount' ? '0.01' : '0'}
                    step={field === 'maxAmount' ? '0.01' : '1'}
                    value={formData[field]}
                    onChange={(e) => {
                      setFormData({ ...formData, [field]: e.target.value });
                      setFormErrors({ ...formErrors, [field]: '' });
                    }}
                    placeholder={placeholder}
                    aria-invalid={!!formErrors[field]}
                    aria-describedby={formErrors[field] ? `${field}-error` : undefined}
                    className={formErrors[field] ? 'border-red-500' : ''}
                  />
                  {formErrors[field] && <p id={`${field}-error`} className="text-red-500 text-xs">{formErrors[field]}</p>}
                </div>
              ))}
            </div>

            <div className="space-y-2">
              <SelectFieldset label="Description">
                <TextareaUI
                  id="description"
                  placeholder="Brief description of this Qardan type"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="border-gray-300 min-h-[100px]"
                  rows={4}
                />
              </SelectFieldset>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDialog(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              style={{ background: '#5B9BD5' }}
              className="text-white"
            >
              {editingItem ? 'Update' : 'Create'} Qardan Type
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        show={false}
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Delete Qardan Type"
        description={
          <>
            Are you sure you want to delete{" "}
            <span className="font-semibold">
              "{ToDelete?.qarzanTypeName}"
            </span>
            ? This action cannot be undone.
          </>
        }
        confirmText="Yes, Delete"
        cancelText="No, Cancel"
        onConfirm={confirmDeleteDraft}
        onCancel={cancelDeleteDraft}
      />
      {/* Delete Confirmation Dialog */}
    </div>
  );
}